import { format } from "date-fns";
import { IntlShape } from "react-intl";
import { Identifiable } from "../../../context";
import { isTableConfig, ListMessages, TableColumns } from "../types";

/**
 * Parameters for CSV data generation
 */
export interface GenerateCSVDataParams<T extends Identifiable> {
  /** Items to export (typically the current visible items) */
  items: T[];

  /** Column definitions */
  columns: TableColumns<T>;

  /** List of hidden column names to exclude from export */
  hiddenColumnNames: string[];

  /** Internationalization messages for column headers */
  messages?: ListMessages;

  /** Intl instance for message formatting */
  intl: IntlShape;

  /** Custom renderers for CSV export */
  CSVRenderers?: { [key: string]: (value: any, fields: any) => string };

  /** Current locale for translations */
  locale?: string;
}

/**
 * Generates CSV-ready data from items based on visible columns.
 *
 * This function:
 * - Exports only the current visible items (not all data from the server)
 * - Respects hidden columns
 * - Uses custom CSV renderers when provided
 * - Handles nested columns (children)
 * - Handles special cases like translationsList
 *
 * @param params - Configuration for CSV generation
 * @returns Array of objects ready for CSVLink component
 *
 * @example
 * ```typescript
 * const csvData = generateCSVData({
 *   items: currentPageItems,
 *   columns,
 *   hiddenColumnNames: ['id'],
 *   messages,
 *   intl,
 *   CSVRenderers: {
 *     date: (value) => format(value, 'dd/MM/yyyy'),
 *   },
 *   locale: 'es-ES',
 * });
 * ```
 */
export function generateCSVData<T extends Identifiable>({
  items,
  columns,
  hiddenColumnNames,
  messages,
  intl,
  CSVRenderers,
  locale,
}: GenerateCSVDataParams<T>): Array<{ [key: string]: any }> {
  let columnsKeys = Object.keys(columns) || [];

  // Handle nested columns (children)
  Object.values(columns).forEach(col => {
    if (isTableConfig(col) && col.children) {
      const childrenKeys = Object.keys(col.children);
      columnsKeys.push(...childrenKeys);
    }
  });

  // Filter out hidden columns
  const orderedFilteredKeys = columnsKeys.filter(key => !hiddenColumnNames.includes(key));

  return items.map(entry =>
    orderedFilteredKeys.reduce<{ [key: string]: any }>((obj, key: keyof T & string) => {
      if (messages?.tableHeaders && messages?.tableHeaders[key]) {
        const headerLabel = intl.formatMessage(messages.tableHeaders[key]);

        // Special case: translations list
        if (key === "translationsList") {
          const shortLocale = locale?.substring(0, 2);
          const currentTranslations =
            entry[key]?.find((it: any) => it.locale === shortLocale) || entry[key]?.[0];

          if (currentTranslations) {
            Object.entries(currentTranslations).forEach(([k, v]) => {
              if (messages?.tableHeaders && messages?.tableHeaders[k]) {
                obj[intl.formatMessage(messages.tableHeaders[k])] = v;
              }
            });
          }
        }
        // Custom CSV renderer
        else if (
          CSVRenderers &&
          Object.keys(CSVRenderers).includes(key) &&
          typeof CSVRenderers[key] === "function"
        ) {
          obj[headerLabel] = CSVRenderers[key](entry[key], entry);
        }
        // Default: use raw value
        else {
          obj[headerLabel] = entry[key];
        }
      }
      return obj;
    }, {})
  );
}

/**
 * Generates default CSV filename with timestamp.
 *
 * The filename format is: `{prefix}_export_{YYYYMMDD}.csv`
 *
 * @param managerName - Optional prefix for filename (e.g., "unitaryProducts")
 * @returns Filename string
 *
 * @example
 * ```typescript
 * generateCSVFilename('products'); // => 'products_export_20260116.csv'
 * generateCSVFilename(); // => 'export_20260116.csv'
 * ```
 */
export function generateCSVFilename(managerName?: string): string {
  const timestamp = format(new Date(), "yyyyMMdd");
  const prefix = managerName || "export";
  return `${prefix}_${timestamp}.csv`;
}
